import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PERMISSION_ACTIONS, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';
import {
  PageLayout,
  Typography,
  Button,
  ICONS,
  PLATFORM_THEME_COLORS,
  TYPOGRAPHY_SIZE,
  DeleteModal,
  Modal,
  EmptyContainer,
  EMPTY_ILLUSTRATIONS,
} from '@thinknet-gmbh/platform-common-ui';

import { ROUTES } from 'src/components/app/app-routes.constants';
import { useGetCompaniesWithJobs } from 'src/hooks/jobs/get-companies-with-jobs.hook';
import { useDeleteJobById } from 'src/hooks/jobs/delete-job-by-id.hook';
import PermissionCheck from 'src/components/permission-check';
import { VacanciesForm } from 'src/pages/vacancies/components/vacancies-form';

import {
  HeaderContainer,
  DepartmentsListTable,
  CompaniesListTable,
  MainContainer,
} from './jobs.styles';
import { JobsListTable } from './components/jobs-list-table';
import { JOBS_MODALS, JOBS_PAGES } from './jobs.constants';
import { checkIfJobsExist } from './jobs.helpers';

const Jobs: React.FC = () => {
  const { t } = useTranslation(['common', 'jobs']);
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [currentJob, setCurrentJob] = useState<any>(null);

  const { companies, paginationConfig, onTableChange } = useGetCompaniesWithJobs();

  const closeModal = () => {
    setCurrentJob(null);
    setActiveModal(null);
  };

  const deleteJobById = useDeleteJobById({ onSuccess: closeModal });

  const onDeleteSubmit = () => {
    deleteJobById({ variables: { jobId: currentJob?.job._id } });
  };

  return (
    <PageLayout pageTitle={t('jobs:labels.jobs')}>
      <Modal
        isOpened={activeModal === JOBS_MODALS.createVacancy}
        title={t('jobs:labels.addNewVacancy')}
        onClose={closeModal}
      >
        <VacanciesForm
          onSuccess={closeModal}
          onClose={closeModal}
          isModal
          initialValues={
            currentJob
              ? {
                  departmentId: currentJob.departmentId,
                  jobId: currentJob.job._id,
                  companyId: currentJob.companyId,
                }
              : undefined
          }
        />
      </Modal>
      <DeleteModal
        isOpened={activeModal === JOBS_MODALS.deleteJob}
        onClose={closeModal}
        title={t('jobs:deleteJob.title')}
        subTitle={t('jobs:deleteJob.subtitle', {
          jobName: currentJob?.job.title,
        })}
        translate={t}
        onSubmit={onDeleteSubmit}
      />
      {/* <SetJobToEmployeeModal
        isOpened={activeModal === JOBS_MODALS.setJobToEmployee}
        onClose={closeModal}
        job={currentJob}
        onFinish={values => console.log(values)}
      /> */}
      <MainContainer>
        <HeaderContainer>
          <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
            {t('jobs:labels.jobs')}
          </Typography>
          <PermissionCheck
            permission={PLATFORM_PERMISSIONS.jobManagement}
            action={PERMISSION_ACTIONS.create}
          >
            <Button
              type="primary"
              icon={ICONS.plus}
              iconColor={PLATFORM_THEME_COLORS.primary.white}
              onClick={() => navigate(`${ROUTES.jobs}/${JOBS_PAGES.createJob}`)}
            >
              {t('jobs:buttons.createJob')}
            </Button>
          </PermissionCheck>
        </HeaderContainer>
        {checkIfJobsExist(companies) ? (
          <CompaniesListTable
            t={t}
            onChange={onTableChange}
            paginationConfig={paginationConfig}
            dataSource={companies}
            expandedRowRender={({ departments, _id: companyId }) => (
              <DepartmentsListTable
                key="departments"
                dataSource={departments.filter((department: any) => department.jobLocations.length)}
                expandedRowRender={({ jobLocations, _id: departmentId }) => (
                  <JobsListTable
                    locationState={{ companyId, departmentId }}
                    jobsLocations={jobLocations}
                    getCurrentJob={(job, modal) => {
                      setCurrentJob({ ...job, departmentId, companyId });
                      setActiveModal(modal);
                    }}
                  />
                )}
              >
                <DepartmentsListTable.Column
                  title={t('common:labels.department')}
                  dataIndex="name"
                  key="name"
                  sorter={(a: any, b: any) => a.name.localeCompare(b.name)}
                  render={name => <Typography>{name}</Typography>}
                />
              </DepartmentsListTable>
            )}
          >
            <CompaniesListTable.Column
              title={t('common:labels.companyName')}
              dataIndex="name"
              key="name"
              sorter
              render={name => <Typography>{name}</Typography>}
            />
          </CompaniesListTable>
        ) : (
          <EmptyContainer
            emptyTitle={t('common:emptyContainer.emptyTitle')}
            contentText={t('common:emptyContainer.contentText')}
            illustration={EMPTY_ILLUSTRATIONS.dataAnalysis}
          />
        )}
      </MainContainer>
    </PageLayout>
  );
};

export default Jobs;
